<template>
  <div class="ticket-item" data-testid="order-item">
    <div class="item-main">
      <p class="emoji" v-html="itemIcon"></p>
      <span class="item-count" data-testid="order-item-qty">{{ item.quantity }}</span>
      <span class="group-name" data-testid="order-item-name" translate="no">{{ displayName }}</span>
    </div>
    <div v-if="item.comment" class="item-comment" data-testid="order-item-comment" translate="no">
      <BzIcon v-if="systemLane" name="chat_bubble" /><i v-else class="pi pi-comment"></i> {{ item.comment }}
    </div>
    <div class="item-details" v-if="item.items">
      <template v-for="(subItem, index) in item.items" :key="index">
        <order-item
          v-if="subItem.items"
          :item="subItem"
        />
        <div v-else class="sub-item-row" data-testid="order-sub-item" translate="no">
          <p class="emoji" v-html="getItemIcon(subItem)"></p>
          <span>{{ subItem.quantity }} x {{ subItem.display_string }}</span>
        </div>
        <div v-if="subItem.comment" class="sub-item-comment" translate="no">
          <BzIcon v-if="systemLane" name="chat_bubble" /><i v-else class="pi pi-comment"></i> {{ subItem.comment }}
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import BzIcon from '../ui/components/BzIcon.vue';
import { systemLane } from '../ui/lane';

const uEmojiParser = require('universal-emoji-parser')

export default {
  name: 'OrderItem',
  components: { BzIcon },
  data() {
    // The service screen this page was painted with (src/ui/lane.js): fixed for the page's life.
    return { systemLane: systemLane() };
  },
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  computed: {
    itemIcon() {
      return this.item.icon
        ? uEmojiParser.parse(this.item.icon)
        : uEmojiParser.parse(':package:');
    },
    displayName() {
      return this.item.display_string;
    }
  },
  methods: {
    getItemIcon(item) {
      return item.icon
        ? uEmojiParser.parse(item.icon)
        : uEmojiParser.parse(':package:');
    }
  }
}
</script>

<style scoped>
.ticket-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
}

.item-main {
  display: flex;
  align-items: center;
  padding: 10px;
  background: var(--bz-p-blue-400-a30);
}

.emoji {
  width: 24px;
  height: 24px;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  margin-right: 10px;
}

.group-name {
  flex-grow: 1;
  text-align: left;
  font-weight: 600;
}

.item-details {
  flex-grow: 1;
  text-align: left;
  background: var(--bz-surface--efeff0-a10);
  padding: 10px;
  padding-left: 44px;
  border-top: 1px solid var(--bz-p-white-a10);
}

.sub-item-row {
  display: flex;
  align-items: center;
  margin: 5px 0;
}

.sub-item-row:first-child {
  margin-top: 0;
}

.sub-item-row:last-child {
  margin-bottom: 0;
}

.item-count {
  background: var(--bz-p-white-a20);
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 12px;
  color: var(--bz-p-white);
  margin-right: 8px;
  font-weight: 800;
}

.item-comment, .sub-item-comment {
  background: var(--bz-p-yellow-400-a15);
  padding: 8px 10px;
  font-size: 13px;
  color: var(--bz-p-yellow-400);
  border-top: 1px solid var(--bz-p-yellow-400-a20);
  font-style: italic;
}

.sub-item-comment {
  margin-left: 44px;
  margin-top: 4px;
  border-radius: 4px;
}

.item-comment i, .sub-item-comment i {
  margin-right: 6px;
  font-size: 12px;
}
/* The ticket on the design system (src/ui/lane.js): the same group with hairline dividers as
   TicketComponent's grouped view; comments in the warning's container, at 14. */
:root[data-bz-lane="system"] .ticket-item {
  margin: 0;
  border-radius: 0;
  border-bottom: 1px solid var(--bz-edge);
}

:root[data-bz-lane="system"] .ticket-item:last-child {
  border-bottom: 0;
}

:root[data-bz-lane="system"] .item-main {
  gap: var(--bz-space-2);
  padding: var(--bz-space-3) 0 var(--bz-space-1);
  background: none;
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
}

:root[data-bz-lane="system"] .emoji {
  margin: 0;
}

:root[data-bz-lane="system"] .item-count {
  margin: 0;
  padding: 0 var(--bz-space-2);
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
  color: var(--bz-ink);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .group-name {
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .item-details {
  padding: 0 0 var(--bz-space-3) var(--bz-space-9);
  border-top: 0;
  background: none;
  color: var(--bz-ink-2);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
}

:root[data-bz-lane="system"] .item-details .ticket-item {
  border-bottom: 0;
}

:root[data-bz-lane="system"] .item-comment,
:root[data-bz-lane="system"] .sub-item-comment {
  margin: var(--bz-space-1) 0 var(--bz-space-2);
  padding: var(--bz-space-2) var(--bz-space-3);
  border: 0;
  border-radius: var(--bz-radius-md);
  background: var(--bz-warn-container);
  color: var(--bz-on-warn-container);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
}

:root[data-bz-lane="system"] .sub-item-comment {
  margin-left: var(--bz-space-9);
}

:root[data-bz-lane="system"] .item-comment .bz-icon,
:root[data-bz-lane="system"] .sub-item-comment .bz-icon {
  margin-right: var(--bz-space-1);
  font-size: var(--bz-icon-xs);
}
</style>